import { K8sProviderIcon } from '@cast/features-cluster-ui';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  notify,
  CloseDialogButton,
  Typography,
  Stack,
} from '@cast/design-system';
import { Cluster } from '@cast/types';
import { HibernationJobType } from '@cast/types-cluster-autoscaler';

import { Link as RouterLink } from 'components/router/Link';
import { EksHibernationWarningAlert } from 'features/organization/cluster-hibernate/components';
import { createHibernationScheduleQueryKey } from 'features/organization/cluster-hibernate/constants';
import { getNodeJobConfigDefaultValue } from 'features/organization/cluster-hibernate/utils';
import { useCreateHibernationJobMutation } from 'hooks/mutations/cluster-autoscaler';

import { isEKSCluster } from '@cast/shared-cluster-context-data-access';

type HibernateClusterDialogProps = {
  cluster: Cluster;
  onClose: () => void;
};

export const HibernateClusterDialog = ({
  cluster,
  onClose,
}: HibernateClusterDialogProps) => {
  const { mutate: createHibernationJob, isPending: isHibernatePending } =
    useCreateHibernationJobMutation();

  const handleHibernate = () => {
    const { nodeConfig } = getNodeJobConfigDefaultValue(cluster.providerType);

    createHibernationJob(
      {
        clusterId: cluster.id,
        hibernationJob: {
          type: HibernationJobType.PAUSE,
          nodeConfig,
        },
      },
      {
        onSuccess: () => {
          notify.info(
            <Stack
              direction="row"
              gap={4}
              data-testid="hibernation-success-message"
            >
              <Typography variant="P14R" color="#fff">
                Hibernating cluster {cluster.name}
              </Typography>
            </Stack>
          );
          onClose();
        },
        onError: () => {
          notify.error(
            <Stack direction="row" gap={4}>
              <Typography variant="P14B" color="red.200">
                Hibernation failed
              </Typography>
              <Typography variant="P14R" color="#fff">
                for {cluster.name}
              </Typography>
            </Stack>
          );
        },
      }
    );
  };

  return (
    <Dialog
      open
      maxWidth="sm"
      testId="hibernate-cluster-dialog"
      onClose={onClose}
    >
      <DialogTitle sx={{ pt: 20 }}>
        <Stack direction="row" alignItems="center" gap={6}>
          <K8sProviderIcon provider={cluster.providerType} size={24} />
          <Typography variant="A1">Hibernate cluster</Typography>
          <Divider orientation="vertical" sx={{ height: '16px' }} />
          <Typography variant="P14R">{cluster.name}</Typography>
        </Stack>
      </DialogTitle>

      <CloseDialogButton onClose={onClose} />

      <DialogContent sx={{ pt: 0 }}>
        <Stack gap={16}>
          <Typography variant="h4">Hibernate cluster now?</Typography>
          <Typography variant="P14R" color="textSecondary">
            Hibernating this cluster will put it into a low-power state, saving
            resources. Once hibernated, the cluster will remain in this state
            until you manually resume it or a defined schedule triggers
            automatic resumption.
          </Typography>
          <Typography variant="P14R" color="textSecondary">
            Define a hibernate schedule for this cluster to automatically
            hibernate and resume.
          </Typography>
          {isEKSCluster(cluster) && <EksHibernationWarningAlert isCompact />}
        </Stack>
      </DialogContent>

      <Divider color="grey.100" />

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button
          testId="add-hibernation-schedule-button"
          variant="text"
          size="lg"
          component={RouterLink}
          to={`/automation/schedules/cluster-hibernation?${createHibernationScheduleQueryKey}=${cluster.id}`}
        >
          Add hibernation schedule
        </Button>
        <Stack direction="row" gap={16}>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            testId="hibernate-cluster-button"
            variant="primary"
            loading={isHibernatePending}
            onClick={handleHibernate}
          >
            Hibernate now
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
